/* eslint-disable */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Circle } from "lucide-react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
} from "@chakra-ui/accordion";
import { useEffect, useState } from "react";
import { createProject, getProjects } from "interfaces/project";
import { getMyProspects } from "interfaces/prospect";
import { set } from "date-fns";
import { getMyInfo } from "interfaces/user";
import ProfileIcon from "components/icons/ProfileIcon";
import { getShowBoMenu, isAdminUser } from "utils/admin";

const MENU_SECTIONS: NonNullable<RoutesType["menuSection"]>[] = ["Repérer", "Prospecter", "Suivre"];

export function SidebarLinks(props: {
    routes: RoutesType[];
    fullSidebar?: boolean;
    onNavClicked: any;
}) {
    const { routes, fullSidebar, onNavClicked } = props;
    const location = useLocation();
    const nav = useNavigate();

    const [prospectsCreated, setProspectsCreated] = useState(0);
    const [me, setMe] = useState<any>(null);
    const [prospectsLimit, setProspectsLimit] = useState(10); // Default limit
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAdminRed, setShowAdminRed] = useState(false);

    const initMyself = async () => {
        const meData = await getMyInfo();
        setMe(meData.data);
        if (meData.data && meData.data.prospectsLimit) {
            setProspectsLimit(meData.data.prospectsLimit);
        }
        setIsAdmin(isAdminUser(meData.data));
        setShowAdminRed(getShowBoMenu());
    };

    const initProspects = async () => {
        try {
            const prospectsData = await getMyProspects();
            if (prospectsData?.data) {
                setProspectsCreated(prospectsData.data.length);
            }
        } catch (error) {
            console.error("Error fetching prospects:", error);
            setProspectsCreated(0);
        }
    };

    // Segment-aware, not `includes`: "/bo" is a prefix of "/bo-commercial", and a substring
    // test would light the Backoffice entry while the commercial one is open.
    const activeRoute = (routeName: string) =>
        location.pathname === routeName || location.pathname.startsWith(routeName + "/");

    // Route icons come with mixed sizes (h-4, h-5, none) — normalise every svg to 18px,
    // centred in a 20px box that matches the label's 20px line-height.
    const iconBox = "flex items-center justify-center w-5 h-5 shrink-0 [&_svg]:w-[18px] [&_svg]:h-[18px]";
    const labelStyles = "text-sm leading-5 truncate";
    // The rail's icons: 18px over a 12px label — the reference mock's proportions, measured, at the app's text-xs.
    const railIconBox = "flex items-center justify-center w-[18px] h-[18px] shrink-0 [&_svg]:!w-[18px] [&_svg]:!h-[18px]";

    // Admin entries: admins only, and only while "Afficher le menu Back-office" is on
    const visible = routes
        .filter((r: any) => r.menu !== false)
        .filter((r: any) => !r.adminOnly || (isAdmin && showAdminRed));

    const createLinks = (routes: RoutesType[]) =>
        routes
            .map((route, key) => {
                const isActive = activeRoute(route.path.toLowerCase());

                // Duckflow design system (see DESIGN_SYSTEM.md §2.6 product shell nav)
                // Fixed 36px row, 20px icon box, 12px icon→label gap, 4px between rows (4px grid).
                // Collapsed sidebar centres the icon in the row instead of padding it from the left.
                const baseStyles = `flex items-center h-9 rounded-lg transition-colors duration-150 mb-1 ${
                    fullSidebar ? "gap-3 px-3" : "justify-center px-0"
                }`;
                const iconColor = isActive ? "text-accent" : "text-ink-muted";
                const textColor = isActive ? "text-ink font-semibold" : "text-ink/75 font-medium";
                const bgColor = isActive ? "bg-surface shadow-card" : "hover:bg-surface-sunken/60";

                if (route.collapse) {
                    return (
                        <Accordion key={key} defaultIndex={0} allowToggle>
                            <AccordionItem border="none">
                                <AccordionButton
                                    onClick={() => nav(route.path)}
                                    className={`${baseStyles} ${bgColor}`}
                                >
                                    <span className={`${iconBox} ${iconColor}`}>
                                        {route.icon}
                                    </span>
                                    {fullSidebar && (
                                        <>
                                            <span className={`${labelStyles} ${textColor}`}>
                                                {route.name}
                                            </span>
                                            <AccordionIcon className="ml-auto text-ink-muted" />
                                        </>
                                    )}
                                </AccordionButton>
                                {fullSidebar && (
                                    <AccordionPanel className="p-0 pb-1">
                                        <ul>{createAccordionLinks(route.items)}</ul>
                                    </AccordionPanel>
                                )}
                            </AccordionItem>
                        </Accordion>
                    );
                }

                // Admin routes styling
                const finalTextColor = route.adminOnly && isAdmin
                    ? "text-danger font-semibold"
                    : textColor;
                const finalIconColor = route.adminOnly && isAdmin
                    ? "text-danger"
                    : iconColor;
                const finalBgColor = route.adminOnly && isAdmin
                    ? (isActive ? "bg-surface shadow-card" : "hover:bg-danger-subtle")
                    : bgColor;
                // Rail item: the icon over a short label, the active one on a white card.
                const railStyles = `flex flex-col items-center justify-center gap-1 rounded-2xl px-1 py-2.5 mb-1 transition-colors duration-150 ${
                    fullSidebar ? "" : "h-11"
                }`;

                return (
                    <Link
                        to={route.layout + route.path}
                        key={key}
                        onClick={() => {
                            // Only close sidebar on mobile (< 1024px)
                            if (window.innerWidth < 1024 && onNavClicked) {
                                onNavClicked();
                            }
                        }}
                        title={route.name}
                        aria-current={isActive ? "page" : undefined}
                        className={`${railStyles} ${finalBgColor}`}
                    >
                        <span className={`${railIconBox} ${route.adminOnly && isAdmin ? finalIconColor : isActive ? "text-accent" : "text-ink-muted"}`}>
                            {route.icon}
                        </span>
                        {fullSidebar && (
                            <span className={`max-w-full truncate text-xs leading-4 ${finalTextColor}`}>
                                {route.menuLabel || route.name}
                            </span>
                        )}
                    </Link>
                );
            });

    const createAccordionLinks = (routes: RoutesType[]) =>
        routes.map((route, index) => {
            const isActive = activeRoute(route.path.toLowerCase());
            const dotColor = isActive ? "text-accent" : "text-ink-faint";
            const textColor = isActive
                ? "text-ink font-medium"
                : "text-ink-muted";

            return (
                <Link
                    key={index}
                    to={route.layout + route.path}
                    onClick={() => {
                        // Only close sidebar on mobile (< 1024px)
                        if (window.innerWidth < 1024 && onNavClicked) {
                            onNavClicked();
                        }
                    }}
                    className="flex items-center gap-3 h-8 px-3 rounded-lg hover:bg-surface-sunken/60 transition-colors duration-150"
                >
                    {/* Dot sits in the same 20px column as the parent icon so labels line up */}
                    <span className="flex items-center justify-center w-5 h-5 shrink-0">
                        <Circle className={`w-1.5 h-1.5 fill-current ${dotColor}`} />
                    </span>
                    <span className={`${labelStyles} ${textColor}`}>{route.name}</span>
                </Link>
            );
        });

    useEffect(() => {
        initProspects();
        initMyself();

        // Listen for storage changes (e.g., from settings)
        const handleStorageChange = () => setShowAdminRed(getShowBoMenu());

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    return (
        <div className="flex flex-col h-full">
            <div>
                {/* Home on top, then the rep's day in three groups, the back-office last */}
                {createLinks(visible.filter((r) => !r.menuSection && !r.adminOnly))}
                {MENU_SECTIONS.map((section) => {
                    const items = visible.filter((r) => r.menuSection === section);
                    if (!items.length) return null;
                    // Groups are told apart by a hairline only; the section names stay in the code.
                    return (
                        <div key={section} role="group" aria-label={section}>
                            <div aria-hidden className="mx-auto my-3.5 h-px w-10 bg-border" />
                            {createLinks(items)}
                        </div>
                    );
                })}
                {visible.some((r) => r.adminOnly) && (
                    <div>
                        <div aria-hidden className="mx-auto my-3.5 h-px w-10 bg-border" />
                        {createLinks(visible.filter((r) => r.adminOnly))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SidebarLinks;
